'use client';

import React, { createContext, use, useState, ReactNode } from 'react';
import type { ClosetItem, ClothingItem } from '@/types/closet';
import { toClosetItem } from '@/lib/closet/transforms';
interface ClosetContextType {
  items: ClosetItem[];
  isLoading: boolean;
  addItem: (item: Omit<ClosetItem, 'id'>) => void;
  updateItem: (id: string, updates: Partial<ClosetItem>) => void;
  removeItem: (id: string) => void;
  setItems: (items: ClosetItem[]) => void;
  refreshItems: () => Promise<void>;
}
const ClosetContext = createContext<ClosetContextType | undefined>(undefined);
export const useCloset = () => {
  const context = use(ClosetContext);
  if (!context) {
    throw new Error('useCloset must be used within a ClosetProvider');
  }
  return context;
};
interface ClosetProviderProps {
  children: ReactNode;
  // Items fetched on the server (closet/page.tsx) so the closet renders with
  // data in the initial HTML — no client fetch waterfall on first paint. Raw
  // API shape (ClothingItem); mapped to ClosetItem once here.
  initialItems?: ClothingItem[];
}
export const ClosetProvider: React.FC<ClosetProviderProps> = ({
  children,
  initialItems
}) => {
  const [items, setItems] = useState<ClosetItem[]>(() => (initialItems ?? []).map(toClosetItem));
  const [isFetching, setIsFetching] = useState(false);
  const refreshItems = async () => {
    setIsFetching(true);
    try {
      const response = await fetch('/api/closet/items');
      if (response.ok) {
        const data = await response.json();
        // Map ClothingItem (from API) to ClosetItem (for context)
        const mappedItems = (data.items || []).map(toClosetItem);
        setItems(mappedItems);
      }
    } catch {
      // Failed to fetch closet items
    }
    // No `finally`: the React Compiler bails on try/finally. The catch above
    // swallows and nothing returns early, so this always runs.
    setIsFetching(false);
  };
  const addItem = (newItem: Omit<ClosetItem, 'id'>) => {
    const itemWithId = {
      ...newItem,
      id: `temp-${Date.now()}`,
      isProcessing: true,
      createdAt: new Date()
    };
    setItems(prev => [itemWithId, ...prev]);
  };
  const updateItem = (id: string, updates: Partial<ClosetItem>) => {
    setItems(prev_0 => prev_0.map(item => item.id === id ? {
      ...item,
      ...updates
    } : item));
  };
  const removeItem = (id_0: string) => {
    setItems(prev_1 => prev_1.filter(item_0 => item_0.id !== id_0));
  };
  const value: ClosetContextType = {
    items,
    isLoading: isFetching,
    addItem,
    updateItem,
    removeItem,
    setItems,
    refreshItems
  };
  return <ClosetContext.Provider value={value}>{children}</ClosetContext.Provider>;
};